import React, { ReactElement } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import classNames from 'classnames/bind';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkDirective from 'remark-directive';
import remarkDirectiveRehype from 'remark-directive-rehype';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import { Text } from 'hast';
import Post, { PostDesignType } from '../../types/post';
import Author from '../../types/author';
import { HastHTMLElement } from '../../types/hastElement';
import type { ImageAttributes, MediaAttributes } from '../../types/customDirective';
import { getPostContentSrc } from '../../utils/postInfo';
import ProfileCard from './ProfileCard';
import Dialogue, { DialogueProps } from './Dialogue';
import Quotation, { QuotationProps } from './Quotation';
import Footnotes from './Footnotes';
import styles from './PostContent.module.scss';

const cx = classNames.bind(styles);

type PostContentProps = {
  post: Post;
};

function PostContent({ post }: PostContentProps): JSX.Element {
  const isInterview = post.type === 'interview';

  return (
    /* [D] interview 일 경우, '-interview' 클래스 추가해주세요. */
    <article className={cx('post_content', { '-interview': isInterview })}>
      <ReactMarkdown
        remarkPlugins={[remarkDirective, remarkDirectiveRehype, remarkGfm]}
        components={{
          /**
           * HTML elements
           */
          p: ({ children }) => {
            // text가 아닌 단일 object를 child로 가지는 경우 <p>로 감싸지 않고 바로 출력
            // (<img>, <figure>등의 element가 <p>로 wrapping되어 표시되는 경우 방지)
            const hasSingleElement = children?.length === 1 ?? false;
            if (hasSingleElement) {
              const child = children[0];
              if (typeof child === 'object') {
                return <>{child}</>;
              }
            }

            return <p>{children}</p>;
          },
          a: ({ children, href, node, ...props }) => {
            const supElement = node.children.find(
              (child) => child.type === 'element' && child.tagName === 'sup'
            ) as HastHTMLElement<'sup'>;

            // 각주의 번호를 나타내는 윗첨자(sup) 링크인 경우 (현재 창에서 열기)
            if (supElement) {
              const supChild = supElement.children[0] as Text;
              const supContent = `[${supChild.value}]`;

              return (
                <Link href={href ?? ''}>
                  <a {...props}>
                    <sup>{supContent}</sup>
                  </a>
                </Link>
              );
            }

            // 그 외 일반적인 링크일 경우 (새 창에서 열기)
            return (
              <Link href={href ?? ''}>
                <a target="_blank" {...props}>
                  {children}
                </a>
              </Link>
            );
          },
          img: ({ src, alt, node, ...props }) => {
            if (src) {
              const fullSrc = getPostContentSrc(src);

              /*
               * next/image는 빌드 후 span > img 형태로 바뀌는 데, 이때 바깥 쪽 span에 className을 지정 못함
               * 따라서 styling을 하기 위해선 wrapper element가 필요
               * (참고: https://stackoverflow.com/a/65538244/6864219)
               *
               * 이떄 div로 감싸지 않고 span으로 감싼 이유는 p tag 안에서 div를 사용할 경우 에러가 발생하기 때문
               * 이로 인해 모든 이미지들이 span > span > img의 바람직하지 못한 구조를 가지게 되지만 현재로썬 이것이 최선
               * 22년 7월 기준 wrapper가 없는 next/future/image가 experimental 단계에 있음
               */
              return (
                <span className={cx('next-image', 'max-width-100')} {...props}>
                  <Image src={fullSrc} alt={alt} quality={92} layout="fill" priority />
                </span>
              );
            }
            return <></>;
          },
          code: ({ node, inline, className, children, ...props }) => {
            // inline code
            if (inline) {
              return (
                <code className={className} {...props}>
                  {children}
                </code>
              );
            }

            // multi-line code
            const match = /language-(\w+)/.exec(className || '');
            const language = match?.[1] ?? undefined;

            return (
              <SyntaxHighlighter language={language} PreTag="pre" style={vscDarkPlus}>
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            );
          },
          pre: ({ children }) => {
            // multi-line code는 <pre>에 두 번 감싸져서 나오는 문제가 있어서 한번 unwrap을 해서 출력
            return <>{children}</>;
          },
          section: ({ children, node, ...props }) => {
            // 각주일 경우 custom component인 <Footnotes> 사용
            const isFootnotes = (node.properties?.className as string[]).includes('footnotes');

            if (isFootnotes) {
              const olElement = node.children.find(
                (childNode) => childNode.type === 'element' && childNode.tagName === 'ol'
              ) as HastHTMLElement<'ol'>;
              const liElements = olElement.children.filter(
                (childNode) => childNode.type === 'element' && childNode.tagName === 'li'
              ) as HastHTMLElement<'li'>[];

              return <Footnotes liElements={liElements} />;
            }

            // 각주가 아닌 section은 일반 <section> element 사용
            return <section {...props}>{children}</section>;
          },
          table: ({ children, node, ...props }) => (
            <div className={cx('table-wrap')}>
              <div className={cx('inner')}>
                <table className={cx('center-aligned')} {...props}>
                  {children}
                </table>
              </div>
            </div>
          ),

          /**
           * custom directives
           */
          // @ts-ignore
          'profile-card': ({ children, designType, name, team, profileImage }: { [key: string]: string }) => {
            const author: Author = {
              name: name || undefined,
              team,
              profileImage: profileImage || undefined,
            };

            return (
              <ProfileCard designType={designType as PostDesignType} author={author}>
                {children}
              </ProfileCard>
            );
          },
          dialogue: ({ children, speaker }: DialogueProps) => <Dialogue speaker={speaker}>{children}</Dialogue>,
          quotation: ({ children, designType }: QuotationProps) => (
            <Quotation designType={designType}>{children}</Quotation>
          ),
          navertv: ({ vid }: { vid: string }) => (
            <div className={cx('video-player')}>
              <iframe
                src={`https://tv.naver.com/embed/${vid}`}
                frameBorder="no"
                scrolling="no"
                marginWidth={0}
                marginHeight={0}
                allowFullScreen
              ></iframe>
            </div>
          ),
          youtube: ({ vid }: { vid: string }) => (
            <div className={cx('video-player')}>
              <iframe
                src={`https://www.youtube.com/embed/${vid}`}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          ),
          // @ts-ignore
          video: ({ src, width = '100%' }: MediaAttributes) => {
            const fullSrc = getPostContentSrc(src);
            return (
              <video
                className={cx('max-width-100', 'center-aligned')}
                loop
                autoPlay
                muted
                preload="auto"
                playsInline
                src={fullSrc}
                style={{ width }}
              ></video>
            );
          },
          // @ts-ignore
          image: ({ src, alt, width = '100%' }: ImageAttributes) => {
            const fullSrc = getPostContentSrc(src);
            return (
              <span className={cx('next-image', 'max-width-100', 'center-aligned')} style={{ width }}>
                <Image src={fullSrc} alt={alt} quality={92} layout="fill" priority />
              </span>
            );
          },
        }}
      >
        {post.content}
      </ReactMarkdown>
    </article>
  );
}

export default PostContent;
